import { TicketAllocation } from '@/app/[festival-slug]/[locale]/(auth)/account/components/TicketRowItem';
import { FilmScreening } from '@/app/[festival-slug]/[locale]/(rest)/films/[slug]/components/ScreeningList';
import { Film } from '@/app/[festival-slug]/[locale]/(rest)/films/[slug]/page';
import { FilmListItem } from '@/components/blocks/FilmCard';
import { Json } from '@/types/database.types';
import { createClient } from '@/utils/supabase/client';
import { create } from 'zustand';
import { useUserStore } from './useUserStore';

export type AddToScheduleFilm = Film | FilmListItem;
export type ScheduleStoreItem = {
    film: AddToScheduleFilm;
    screening?: FilmScreening;
    ticket?: TicketAllocation;
};

type SavedScheduleItem = { filmSlug: string; screeningId?: string };

const getLocationLabel = (value: unknown, locale: string) => {
    if (!value) return '';
    if (typeof value === 'string') return value;

    if (typeof value === 'object') {
        const record = value as Record<string, string | null>;
        return (
            record[locale] ||
            record.en ||
            record.fr ||
            Object.values(record).find((entry) => Boolean(entry)) ||
            ''
        );
    }

    return '';
};

const findScreeningOnFilm = (film: Film, screeningId?: string) => {
    if (!screeningId) {
        return film.screenings?.[0] ?? film.short_programme?.screenings?.[0];
    }

    return (
        film.screenings?.find((screening) => screening.id === screeningId) ??
        film.short_programme?.screenings?.find((screening) => screening.id === screeningId)
    );
};

interface ScheduleState {
    items: ScheduleStoreItem[];
    loading: boolean;
    setItems: (items: ScheduleStoreItem[]) => void;
    addItem: (item: ScheduleStoreItem) => void;
    removeItem: (film: AddToScheduleFilm, screeningId?: string) => void;
    clear: () => void;
    fetchSchedule: (userId: string, locale: string) => void;
}

const useScheduleStore = create<ScheduleState>()((set) => {
    const supabase = createClient();
    const saveItemsToSupabase = async (items: ScheduleStoreItem[]) => {
        const { user } = useUserStore.getState();
        if (!user) return;

        const itemsToSave = items.map((item) => ({
            filmSlug: item.film.slug,
            ...(item.screening?.id && { screeningId: item.screening.id }),
        }));

        const { error } = await supabase
            .from('profiles')
            .upsert(
                {
                    user_id: user.id,
                    schedule: itemsToSave as unknown as Json[],
                },
                {
                    onConflict: 'user_id',
                },
            );

        if (error) console.error('Error saving items:', error);
    };

    return {
        items: [],
        loading: false,
        setItems: (items) => set({ items }),
        addItem: ({ film, screening }) =>
            set((state) => {
                const isExisting = state.items.some((item) =>
                    screening ? item?.screening?.id === screening.id : item.film.id === film.id,
                );
                if (isExisting) return state;

                const newItems = [...state.items, { film, screening }];

                saveItemsToSupabase(newItems);

                return { items: newItems };
            }),

        removeItem: (film, screeningId) =>
            set((state) => {
                const newItems = state.items.filter((item) =>
                    screeningId ? item.screening?.id !== screeningId : item.film.id !== film.id,
                );
                saveItemsToSupabase(newItems);

                return { items: newItems };
            }),
        clear: () => set({ items: [] }),
        fetchSchedule: async (userId: string, locale: string) => {
            set({ loading: true });

            const { data, error } = await supabase
                .from('profiles')
                .select('schedule')
                .eq('user_id', userId)
                .maybeSingle();

            if (error) {
                console.error('Error loading schedule:', error);
                set({ items: [] });
                return;
            }

            if (!data) {
                set({ items: [] });
                set({ loading: false });
                return;
            }

            const scheduleData = (data.schedule as SavedScheduleItem[] | null) || [];
            if (scheduleData.length === 0) {
                set({ items: [], loading: false });
                return;
            }

            const filmsSlugs = Array.from(new Set(scheduleData.map((item) => item.filmSlug)));

            const { data: filmsData, error: filmsError } = await supabase
                .rpc('get_films_bulk', {
                    film_slugs: filmsSlugs,
                    language_key: locale,
                })
                .returns<Film[]>();

            if (filmsError) {
                console.error('Error fetching films:', filmsError);
            }

            const filmsBySlug = new Map((filmsData || []).map((film) => [film.slug, film]));
            const missingSlugs = filmsSlugs.filter((slug) => !filmsBySlug.has(slug));

            if (missingSlugs.length > 0) {
                const missingFilms = await Promise.all(
                    missingSlugs.map(async (slug) => {
                        const { data: filmData, error: filmError } = await supabase
                            .rpc('get_film', {
                                film_slug: slug,
                                language_key: locale,
                            })
                            .returns<Film[]>();

                        if (filmError) {
                            console.error('Error fetching schedule film:', slug, filmError);
                        }

                        return filmData?.[0];
                    }),
                );

                missingFilms.forEach((film) => {
                    if (film?.slug) {
                        filmsBySlug.set(film.slug, film);
                    }
                });
            }

            const missingScreeningIds = Array.from(
                new Set(
                    scheduleData
                        .map((saved) => {
                            const film = filmsBySlug.get(saved.filmSlug);
                            if (!film || !saved.screeningId) return undefined;
                            return findScreeningOnFilm(film, saved.screeningId)
                                ? undefined
                                : saved.screeningId;
                        })
                        .filter((id): id is string => Boolean(id)),
                ),
            );

            const extraScreeningsById = new Map<string, FilmScreening>();

            if (missingScreeningIds.length > 0) {
                const { data: extraScreenings, error: extraScreeningsError } = await supabase
                    .from('screenings')
                    .select(
                        'id, start_time, screening_type, special_price, ticket_sales_disabled, locations(id, name, address)',
                    )
                    .in('id', missingScreeningIds);

                if (extraScreeningsError) {
                    console.error('Error fetching past schedule screenings:', extraScreeningsError);
                }

                extraScreenings?.forEach((row) => {
                    extraScreeningsById.set(row.id, {
                        id: row.id,
                        start_time: row.start_time || '',
                        screening_type: row.screening_type as FilmScreening['screening_type'],
                        special_price: row.special_price,
                        ticket_sales_disabled: row.ticket_sales_disabled,
                        location: {
                            id: row.locations?.id || '',
                            name: getLocationLabel(row.locations?.name, locale),
                            address: row.locations?.address || '',
                        },
                    });
                });
            }

            const scheduleItems = scheduleData.reduce<ScheduleStoreItem[]>((prev, saved) => {
                const film = filmsBySlug.get(saved.filmSlug);
                if (!film) return prev;

                const screening =
                    findScreeningOnFilm(film, saved.screeningId) ??
                    (saved.screeningId ? extraScreeningsById.get(saved.screeningId) : undefined);

                return [
                    ...prev,
                    {
                        film,
                        screening,
                    },
                ];
            }, []);

            set({ items: scheduleItems, loading: false });
        },
    };
});

export const isItemInSchedule =
    (film: AddToScheduleFilm, screeningId?: string) =>
    ({ items }: ScheduleState): boolean =>
        items.some((item) =>
            screeningId ? item.screening?.id === screeningId : item.film.id === film.id,
        );

export default useScheduleStore;
